import { useCallback, useEffect, useState } from 'react';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Hidden from '@material-ui/core/Hidden';
import Skeleton from '@material-ui/lab/Skeleton';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { TrainingCategoryResponse, TypeTraining } from '../../services/models';
import { TrainingService } from '../../services';
import { useLocalStorage, useQuery } from '../../hooks';
import { formatError, snackbarUtils } from '../../utils';

import Layout from '../../layout';
import Head from '../../components/Head';
import TrainingCard from './Card';
import NoAppointments from './NoAppointments';
import useStyles from './styles';
import TrainingCarousel from './Carousel';

const TrainingList = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const query = useQuery();

  const [loading, setLoading] = useState(true);
  const [trainingsState, setTrainingsState] = useLocalStorage<TrainingCategoryResponse[]>('trainingsState', []);
  const [trainings, setTrainings] = useState<TrainingCategoryResponse[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    async function getTraining() {
      try {
        const { data } = await TrainingService.getTrainingByCategory()
        setTrainings(data);
        setTrainingsState(data);
      } catch (error) {
        console.error('getTraining: ', error);
        snackbarUtils.error(formatError(error));
      } finally {
        setLoading(false)
      }
    }
    getTraining()
  }, [setTrainingsState]);

  /**
   * Realiza filtros e altera o estado que armazena os treinamentos.
   * + Checagens Realizadas:
   *  - 1° Categoria "all", reseta o estado.
   *  - 2° Categoria "viewed", busca os treinamentos visualizados
   *  - 3° Categoria "not-viewed", busca os treinamentos não visualizados
   *  - 4° Busca as Categorias pelo nome
   * @param {string} category Categoria que define qual filtro será realizado
   **/
  const handleCategory = useCallback((category: string) => {
    setSelectedCategory(category);
    if (trainingsState) {
      if (category === "all") setTrainings(trainingsState);
      else if (category === "viewed") {
        const filteredTrainings = trainingsState.map((trainingsCategories) => trainingsCategories.training.filter((training => training.viewed === true)))
        let auxTrainings: TrainingCategoryResponse[] = [{ ...trainingsState[0] }];
        auxTrainings[0].description = "Visualizados"
        auxTrainings[0].training = [...filteredTrainings.flat()]
        setTrainings(auxTrainings);
      }
      else if (category === "not-viewed") {
        const filteredTrainings = trainingsState.map((trainingsCategories) => trainingsCategories.training.filter((training => training.viewed === false)))
        let auxTrainings: TrainingCategoryResponse[] = [{ ...trainingsState[0] }];
        auxTrainings[0].description = "Não Visualizados"
        auxTrainings[0].training = [...filteredTrainings.flat()]
        setTrainings(auxTrainings);
      }
      else {
        const filteredTrainings = trainingsState.filter((training) => training.description === category)
        setTrainings(filteredTrainings);
      }
    }
  }, [trainingsState])

  useEffect(() => {
    const category = query.get('category')
    if (category) {
      setSelectedCategory(category);
      handleCategory(category);
    } else return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCategory]);

  const renderTrainings = useCallback((trainings: TypeTraining[]) => {
    return isMobile ? (
      <TrainingCarousel trainings={trainings} />
    ) : (
      trainings.length !== 0 ? (
        trainings.map((training) => (
          <Grid key={training.id} item xs={6} md={4}>
            <TrainingCard training={training} />
          </Grid>
        )
        )) : (
        <Typography variant='subtitle2'>Sem Treinamentos</Typography>
      )
    )
  }, [isMobile]);

  return (
    <Layout page="training" innerPage>
      <Head>Treinamentos</Head>
      {loading && (
        Array.from(Array(2)).map((i, index) => (
          <Box key={`schedules-skeletons-${index}`} width="100%" marginY={0.5}>
            <Skeleton variant="text" />
            <Skeleton variant="rect" width="100%" height={235} />
          </Box>
        ))
      )}
      {trainings.length !== 0 ? (
        <Container component="section" className={classes.container}>
          <Grid container spacing={2} alignItems="center">
            <Hidden only="xs">
              <Grid item xs={8}>
                <Typography variant="h2">O que você precisa saber para trabalhar de forma produtiva e segura.</Typography>
              </Grid>
            </Hidden>
            <Grid container item xs={12} sm={4} className={classes.selectContainer}>
              <FormControl className={classes.formControl}>
                <InputLabel htmlFor="training-category-select">Categorias</InputLabel>
                <Select
                  labelId="training-category-select-label"
                  id="training-category-select"
                  value={selectedCategory}
                  onChange={(event) => handleCategory(event.target.value as string)}
                  className={classes.select}
                  inputProps={{
                    classes: {
                      icon: classes.selectIcon,
                    },
                  }}
                >
                  <MenuItem value="all">Todas as Categorias</MenuItem>
                  <MenuItem value="viewed">Visualizados</MenuItem>
                  <MenuItem value="not-viewed">Não Visualizados</MenuItem>
                  {trainingsState?.map(training => (
                    <MenuItem key={training.id} value={training.description}>
                      {training.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} className={classes.contentContainer}>
            {trainings.map((category) => (
              <Grid key={category.id} container item xs={12}>
                <Typography variant="h3">{category.description}</Typography>
                <Grid container item xs={12} spacing={3}>
                  {renderTrainings(category.training)}
                </Grid>
              </Grid>
            ))
            }
          </Grid>
        </Container>
      ) : (
        !loading && <NoAppointments />
      )}
    </Layout>
  );
}

export default TrainingList;